import {
  CraAttributes,
  CraAttributeName,
  CraAttributeSelect,
  CodingAttributes,
} from "@keita/lanepatrol-sdk";
import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import { OsaNode } from "../../osaway/models/OsaNode";
import { OsaWayApi } from "../../osaway/services/OsaWayApi";
import { downloadFile } from "../../shared/utils/file-utils";
import { OsaWay } from "../../osaway/models/OsaWay";

enum Col {
  coderName,
  codingDate,
  projectName,
  projectCity,
  routeName,
  photoDate,
  photoReference,
  photoName,
  distanceFromStart,
  distancetoNext,
  latitudeStart,
  longitudeStart,
  latitudeEnd,
  longitudeEnd,
  scoreGlobal,
  scoreVB,
  scoreBB,
  scoreBP,
  scoreSB,
  comment,
  areaType,
  facilityType,
  facilityAccess,
  looseOrSlipperySurface,
  tramOrTrainRails,
  majorSurfaceDeformationOrDrainOpening,
  fixedObstacleOnFacility,
  nonFixedObstacleOnFacility,
  lineOfSight,
  delineation,
  lightSegregation,
  facilityWidthPerDirection,
  flowDirection,
  widthRestriction,
  roadLane01,
  vehicleParking01,
  severeHazard01,
  objectOrLevelChange01,
  sidewalk01,
  roadLane13,
  vehicleParking13,
  severeHazard13,
  objectOrLevelChange13,
  sidewalk13,
  grade,
  curvature,
  streetLighting,
  pedestrianCrossing,
  intersectionBicycleFacility,
  intersectionApproach,
  intersectionOrRoadCrossing,
  crossingFacility,
  numberOfLanesAdjacentRoad,
  numberOfLanesIntersectingRoad,
  propertyAccess,
  peakPedestrianFlowAlongOrAcrossFacility,
  peakBicycleAndLVTrafficFlow,
  observedProportionOfCargoBikesAndMopeds,
  bicycleAndLVSpeedAverage,
  bicycleAndLVSpeedDifferential,
  roadAADT,
  heavyVehicleFlow,
  roadSpeedLimit,
  roadOperatingSpeed,
}

type osaWayId = string;
type nodeId = string;

export const codingExport = async (
  osaWayIds: string[],
  overwriteValues?: {
    allNodeCodingsByWayId: Map<osaWayId, Map<nodeId, OsaNodeCoding>>;
    osaNodes: Map<osaWayId, OsaNode[]>;
    osaWays: OsaWay[];
  },
) => {
  var finalCsvText = "";

  for (const osaWayId of osaWayIds) {
    const { data: blob } = await OsaWayApi.getCodingCsv(osaWayId);
    const csvText = await blobToText(blob);

    const csvLines = csvText.split("\n");
    const headerLine = csvLines[0];
    const dataLines = csvLines.slice(1);

    if (overwriteValues) {
      const roadCodingEntries =
        overwriteValues.allNodeCodingsByWayId.get(osaWayId)!;
      const nodes = overwriteValues.osaNodes.get(osaWayId)!;

      dataLines.forEach((lineStr, index) => {
        const line = lineStr.trim();
        if (line) {
          const node = nodes[index]!;
          dataLines[index] = overwriteLine(
            line,
            roadCodingEntries.get(node.id)!.attributes,
            nodes[index],
          );
        }
      });
    }

    if (finalCsvText) {
      finalCsvText += dataLines.join("\n");
    } else {
      finalCsvText += [headerLine, ...dataLines].join("\n");
    }
  }

  downloadFile(new Blob([finalCsvText]), `Coding_${new Date().getTime()}.csv`);
};

const overwriteLine = (
  line: string,
  attributes: CodingAttributes,
  node: OsaNode,
): string => {
  const values = line.split(",");

  values[Col.coderName] = "";
  values[Col.codingDate] = "";

  if (node.score) {
    values[Col.scoreGlobal] = node.score.global.value.toFixed(2);
    values[Col.scoreBB] = node.score.bicycleToBicycle.value.toFixed(2);
    values[Col.scoreBP] = node.score!.bicycleToPedestrian.value.toFixed(2);
    values[Col.scoreSB] = node.score.singleBicycle.value.toFixed(2);
    values[Col.scoreVB] = node.score.vehicleToBicycle.value.toFixed(2);
  }

  values[Col.areaType] = getOptionIndex(
    CraAttributeName.AreaType,
    attributes.areaType,
  );
  values[Col.facilityType] = getOptionIndex(
    CraAttributeName.FacilityType,
    attributes.facilityType,
  );
  values[Col.facilityAccess] = getOptionIndex(
    CraAttributeName.FacilityAccess,
    attributes.facilityAccess,
  );
  values[Col.looseOrSlipperySurface] = getOptionIndex(
    CraAttributeName.LooseOrSlipperySurface,
    attributes.looseOrSlipperySurface,
  );
  values[Col.tramOrTrainRails] = getOptionIndex(
    CraAttributeName.TramOrTrainRails,
    attributes.tramOrTrainRails,
  );
  values[Col.majorSurfaceDeformationOrDrainOpening] = getOptionIndex(
    CraAttributeName.MajorSurfaceDeformationOrDrainOpening,
    attributes.majorSurfaceDeformationOrDrainOpening,
  );
  values[Col.fixedObstacleOnFacility] = getOptionIndex(
    CraAttributeName.FixedObstacleOnFacility,
    attributes.fixedObstacleOnFacility,
  );
  values[Col.nonFixedObstacleOnFacility] = getOptionIndex(
    CraAttributeName.NonFixedObstacleOnFacility,
    attributes.nonFixedObstacleOnFacility,
  );
  values[Col.lineOfSight] = getOptionIndex(
    CraAttributeName.LineOfSight,
    attributes.lineOfSight,
  );
  values[Col.delineation] = getOptionIndex(
    CraAttributeName.Delineation,
    attributes.delineation,
  );
  values[Col.lightSegregation] = getOptionIndex(
    CraAttributeName.LightSegregation,
    attributes.lightSegregation,
  );
  values[Col.facilityWidthPerDirection] = getOptionIndex(
    CraAttributeName.FacilityWidthPerDirection,
    attributes.facilityWidthPerDirection,
  );
  values[Col.flowDirection] = getOptionIndex(
    CraAttributeName.FlowDirection,
    attributes.flowDirection,
  );
  values[Col.widthRestriction] = getOptionIndex(
    CraAttributeName.WidthRestriction,
    attributes.widthRestriction,
  );
  values[Col.roadLane01] = getOptionIndex(
    CraAttributeName.AdjacentFeatures0to1mRoadLane,
    attributes.adjacentFeatures0to1m?.roadLane ?? null,
  );
  values[Col.vehicleParking01] = getOptionIndex(
    CraAttributeName.AdjacentFeatures0to1mVehicleParking,
    attributes.adjacentFeatures0to1m?.vehicleParking ?? null,
  );
  values[Col.severeHazard01] = getOptionIndex(
    CraAttributeName.AdjacentFeatures0to1mSevereHazard,
    attributes.adjacentFeatures0to1m?.severeHazard ?? null,
  );
  values[Col.objectOrLevelChange01] = getOptionIndex(
    CraAttributeName.AdjacentFeatures0to1mObjectOrLevelChange,
    attributes.adjacentFeatures0to1m?.objectOrLevelChange ?? null,
  );
  values[Col.sidewalk01] = getOptionIndex(
    CraAttributeName.AdjacentFeatures0to1mSidewalk,
    attributes.adjacentFeatures0to1m?.sidewalk ?? null,
  );
  values[Col.roadLane13] = getOptionIndex(
    CraAttributeName.AdjacentFeatures1to3mRoadLane,
    attributes.adjacentFeatures1to3m?.roadLane ?? null,
  );
  values[Col.vehicleParking13] = getOptionIndex(
    CraAttributeName.AdjacentFeatures1to3mVehicleParking,
    attributes.adjacentFeatures1to3m?.vehicleParking ?? null,
  );
  values[Col.severeHazard13] = getOptionIndex(
    CraAttributeName.AdjacentFeatures1to3mSevereHazard,
    attributes.adjacentFeatures1to3m?.severeHazard ?? null,
  );
  values[Col.objectOrLevelChange13] = getOptionIndex(
    CraAttributeName.AdjacentFeatures1to3mObjectOrLevelChange,
    attributes.adjacentFeatures1to3m?.objectOrLevelChange ?? null,
  );
  values[Col.sidewalk13] = getOptionIndex(
    CraAttributeName.AdjacentFeatures1to3mSidewalk,
    attributes.adjacentFeatures1to3m?.sidewalk ?? null,
  );
  values[Col.grade] = getOptionIndex(CraAttributeName.Grade, attributes.grade);
  values[Col.curvature] = getOptionIndex(
    CraAttributeName.Curvature,
    attributes.curvature,
  );
  values[Col.streetLighting] = getOptionIndex(
    CraAttributeName.StreetLighting,
    attributes.streetLighting,
  );
  values[Col.pedestrianCrossing] = getOptionIndex(
    CraAttributeName.PedestrianCrossing,
    attributes.pedestrianCrossing,
  );
  values[Col.intersectionBicycleFacility] = getOptionIndex(
    CraAttributeName.IntersectionBicycleFacility,
    attributes.intersectionBicycleFacility,
  );
  values[Col.intersectionApproach] = getOptionIndex(
    CraAttributeName.IntersectionApproach,
    attributes.intersectionApproach,
  );
  values[Col.intersectionOrRoadCrossing] = getOptionIndex(
    CraAttributeName.IntersectionOrRoadCrossing,
    attributes.intersectionOrRoadCrossing,
  );
  values[Col.crossingFacility] = getOptionIndex(
    CraAttributeName.CrossingFacility,
    attributes.crossingFacility,
  );
  values[Col.numberOfLanesAdjacentRoad] = getOptionIndex(
    CraAttributeName.NumberOfLanesAdjacentRoad,
    attributes.numberOfLanesAdjacentRoad,
  );
  values[Col.numberOfLanesIntersectingRoad] = getOptionIndex(
    CraAttributeName.NumberOfLanesIntersectingRoad,
    attributes.numberOfLanesIntersectingRoad,
  );
  values[Col.propertyAccess] = getOptionIndex(
    CraAttributeName.PropertyAccess,
    attributes.propertyAccess,
  );
  values[Col.peakPedestrianFlowAlongOrAcrossFacility] = getOptionIndex(
    CraAttributeName.PeakPedestrianFlowAlongOrAcrossFacility,
    attributes.peakPedestrianFlowAlongOrAcrossFacility,
  );
  values[Col.peakBicycleAndLVTrafficFlow] = getOptionIndex(
    CraAttributeName.PeakBicycleAndLVTrafficFlow,
    attributes.peakBicycleAndLVTrafficFlow,
  );
  values[Col.observedProportionOfCargoBikesAndMopeds] = getOptionIndex(
    CraAttributeName.ObservedProportionOfCargoBikesAndMopeds,
    attributes.observedProportionOfCargoBikesAndMopeds,
  );
  values[Col.bicycleAndLVSpeedAverage] = getOptionIndex(
    CraAttributeName.BicycleAndLVSpeedAverage,
    attributes.bicycleAndLVSpeedAverage,
  );
  values[Col.bicycleAndLVSpeedDifferential] = getOptionIndex(
    CraAttributeName.BicycleAndLVSpeedDifferential,
    attributes.bicycleAndLVSpeedDifferential,
  );
  values[Col.roadAADT] = getOptionIndex(
    CraAttributeName.RoadAADT,
    attributes.roadAADT,
  );
  values[Col.heavyVehicleFlow] = getOptionIndex(
    CraAttributeName.HeavyVehicleFlow,
    attributes.heavyVehicleFlow,
  );
  values[Col.roadSpeedLimit] = attributes.roadSpeedLimit?.toString() ?? "";
  values[Col.roadOperatingSpeed] =
    attributes.roadOperatingSpeed?.toString() ?? "";

  return values.join(",");
};

const getOptionIndex = (
  attributeName: CraAttributeName,
  value: string | null,
): string => {
  if (value === null) return "";
  const attribute = CraAttributes.getByName(
    attributeName,
  ) as CraAttributeSelect;
  return (
    attribute.options.findIndex((option) => option.value === value) + 1
  ).toString();
};

const blobToText = async (blob: Blob) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result?.toString() ?? "");
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
};
